import { useAdminPhotoSettings } from '@/features/photos/photoSources'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type CourseDto, type CourseSave, type CertificateDesignDto, type CertificateFieldConfig, type CourseMaterialDto } from '@/api/admin'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { useTopBar } from '@/contexts/TopBarContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Upload, Link2, FileText, File, FileVideo,
  Download, ExternalLink, Trash2, X, BookOpen,
  Search, UserCheck, Check,
} from 'lucide-react'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import { useAdminMoney } from '@/hooks/useMoney'
import { formatDate } from '@/lib/dates'
import { CourseLiveActivitiesTab } from '@/features/live/CourseLiveActivitiesTab'
import CourseDurationFields from './CourseDurationFields'
import CourseCategorySelect from './CourseCategorySelect'
import WebsiteTab from './WebsiteTab'
import { normaliseOwnPage } from './ownPage'
import { categoryErrorMessage } from './categoryErrors'
import UnsavedBar from '@/components/shared/UnsavedBar'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MAX_FILE_MB = 25
const MAX_COURSE_MB = 100

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isYouTubeUrl(url: string): boolean {
  return /(?:youtube\.com\/watch|youtu\.be\/)/.test(url)
}

function youTubeEmbedUrl(url: string): string | null {
  const m =
    url.match(/youtube\.com\/watch\?v=([^&]+)/) ??
    url.match(/youtu\.be\/([^?]+)/)
  return m ? `https://www.youtube.com/embed/${m[1]}` : null
}

function isPdf(contentType: string | null): boolean {
  return contentType === 'application/pdf'
}

function MaterialIcon({ contentType }: { contentType: string | null }) {
  if (!contentType) return <File size={16} className="text-gray-400" />
  if (contentType === 'application/pdf') return <FileText size={16} className="text-red-500" />
  if (contentType.startsWith('video/')) return <FileVideo size={16} className="text-purple-500" />
  if (contentType.startsWith('image/')) return <FileText size={16} className="text-blue-500" />
  return <File size={16} className="text-gray-400" />
}

// ─── Certificate design preview ───────────────────────────────────────────────

function parseFields(json: string | null): CertificateFieldConfig[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function previewText(cfg: CertificateFieldConfig, companyName: string | null, region?: string | null): string {
  const today = new Date()
  const nextYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
  const fmt = (d: Date) => formatDate(d, region, { day: 'numeric', month: 'long', year: 'numeric' })
  switch (cfg.field) {
    case 'recipientName':  return 'Recipient Name'
    case 'courseName':     return 'Course Name'
    case 'courseCode':     return 'CODE-01'
    case 'completedDate':  return fmt(today)
    case 'expiryDate':     return fmt(nextYear)
    case 'companyName':    return companyName || 'Your Company'
    case 'static':         return cfg.staticText || 'Custom text'
    default:               return ''
  }
}

function CertificateDesignPreview({ design }: { design: CertificateDesignDto }) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const [canvasH, setCanvasH] = useState(0)

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const obs = new ResizeObserver(() => setCanvasH(el.offsetHeight))
    obs.observe(el)
    setCanvasH(el.offsetHeight)
    return () => obs.disconnect()
  }, [])

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const fields = parseFields(design.fieldsJson)
  const companyName = profile?.displayName ?? null
  const region = profile?.region

  return (
    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white" style={{ width: 240 }}>
      <div ref={canvasRef}
        className="relative overflow-hidden bg-gray-100 select-none"
        style={{ aspectRatio: design.isPortrait ? '1 / 1.414' : '1.414 / 1' }}
      >
        {(() => {
          const bgUrl = design.templateBackgroundImageUrl ?? design.systemTemplateThumbnailUrl ?? null
          return bgUrl
            ? <>
                <img src={bgUrl} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
                {design.systemCertificateTemplateId && design.primaryColour && (
                  <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: design.primaryColour, mixBlendMode: 'screen' }} />
                )}
              </>
            : <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-xs">No background</div>
        })()}

        {fields.map((el, i) => {
          const isLogo = el.field === 'companyLogo'
          return (
            <div key={i}
              className="absolute pointer-events-none"
              style={{
                left: `${el.x}%`, top: `${el.y}%`,
                transform: 'translate(-50%, -50%)',
                ...((isLogo || el.field === 'image')
                  ? { width: `${isLogo ? (el.logoWidth ?? 20) : (el.imageWidth ?? 20)}%` }
                  : {
                    fontSize: canvasH ? `${el.fontSize * (canvasH / 1000)}px` : undefined,
                    fontWeight: el.bold ? 700 : 400,
                    color: el.color,
                    textAlign: el.align as React.CSSProperties['textAlign'],
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    textShadow: '0 1px 2px rgba(255,255,255,0.5)',
                  }),
              }}
            >
              {isLogo
                ? profile?.logoUrl
                  ? <img src={profile.logoUrl} alt="" className="w-full h-auto" draggable={false} />
                  : <div className="border border-dashed border-gray-400/60 rounded px-1 py-0.5 text-gray-400 text-[9px] whitespace-nowrap">Logo</div>
                : el.field === 'image'
                  ? el.imageUrl
                    ? <img src={el.imageUrl} alt="" className="w-full h-auto" draggable={false} />
                    : null
                  : previewText(el, companyName, region)}
            </div>
          )
        })}
      </div>
      <div className="px-2.5 py-1.5 flex items-center justify-between border-t border-gray-100 bg-gray-50">
        <span className="text-xs font-medium text-gray-700 truncate">{design.name}</span>
      </div>
    </div>
  )
}

// ─── Details tab ──────────────────────────────────────────────────────────────

type CourseFormData = CourseSave

function Field({ id, label, children }: { id?: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  )
}

function toCourseForm(course: CourseDto): CourseFormData {
  return {
    name: course.name,
    code: course.code,
    category: course.category,
    categoryId: course.categoryId,
    description: course.description,
    websiteUrl: course.websiteUrl,
    price: course.price,
    durationMinutes: course.durationMinutes,
    defaultDays: course.defaultDays,
    durationLabel: course.durationLabel,
    defaultExpiryMonths: course.defaultExpiryMonths,
    certificateDesignId: course.certificateDesignId,
    isThirdPartyCertified: course.isThirdPartyCertified,
    thirdPartyProviderName: course.thirdPartyProviderName,
    sendThirdPartyCompletionEmail: course.sendThirdPartyCompletionEmail,
    pricingModel: course.pricingModel,
    flatFeeAmount: course.flatFeeAmount,
    flatFeeCapacity: course.flatFeeCapacity,
    requirePhotoCheck: course.requirePhotoCheck,
  }
}

function DetailsTab({ course, onDirtyChange }: { course: CourseDto; onDirtyChange?: (dirty: boolean) => void }) {
  const { symbol } = useAdminMoney()
  const photoSettings = useAdminPhotoSettings()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<CourseFormData>(() => toCourseForm(course))
  const [saved, setSaved] = useState(false)
  const [showProDialog, setShowProDialog] = useState(false)
  const [ownPageError, setOwnPageError] = useState<string | null>(null)

  // Unsaved when the form differs from what is saved. Told to the page, so leaving the tab can warn.
  const baseline = toCourseForm(course)
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline)
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])

  const { data: designs = [] } = useQuery({
    queryKey: ['certificate-designs'],
    queryFn: () => adminApi.getCertificateDesigns().then(r => r.data),
  })
  const { data: companyProfile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })
  const isPro = companyProfile?.planTier === 'Pro'

  const updateMutation = useMutation({
    mutationFn: (data: CourseFormData) => adminApi.updateCourse(course.id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['course', course.id] })
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      // Show what the server actually kept (it trims text and tidies the address), so nothing looks unsaved.
      const fresh = queryClient.getQueryData<CourseDto>(['course', course.id])
      if (fresh) setForm(toCourseForm(fresh))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  const set = (key: keyof CourseFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : value }))
  const setNum = (key: keyof CourseFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : parseFloat(value) }))
  const setInt = (key: keyof CourseFormData, value: string) =>
    setForm(f => ({ ...f, [key]: value === '' ? null : parseInt(value) }))

  const selectedDesign = designs.find(d => d.id === form.certificateDesignId)

  return (
    <>
    <form onSubmit={e => {
      e.preventDefault()
      // Tidy the page address first (adds https:// if it was left off); stop if it can't be used.
      const ownPage = normaliseOwnPage(form.websiteUrl)
      setOwnPageError(ownPage.error)
      if (ownPage.error) return
      setForm(f => ({ ...f, websiteUrl: ownPage.value }))
      updateMutation.mutate({ ...form, websiteUrl: ownPage.value })
    }} className="space-y-5 max-w-2xl">
      {/* Row 1: Name + Code */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="col-span-2">
          <Field id="name" label="Course name *">
            <Input id="name" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Manual Handling" />
          </Field>
        </div>
        <Field id="code" label="Course code">
          <Input id="code" value={form.code ?? ''} onChange={e => set('code', e.target.value)} placeholder="e.g. MH-01" />
        </Field>
      </div>

      {/* Row 2: Category + Expiry */}
      <div className="grid grid-cols-2 gap-4">
        <Field id="category" label="Category">
          <CourseCategorySelect
            id="category"
            categoryId={form.categoryId}
            legacyName={form.category}
            onChange={(categoryId, category) => setForm(f => ({ ...f, categoryId, category }))}
          />
        </Field>
        <Field id="defaultExpiryMonths" label="Renews every (months)">
          <Input id="defaultExpiryMonths" type="number" min="1" step="1" value={form.defaultExpiryMonths ?? ''} onChange={e => setInt('defaultExpiryMonths', e.target.value)} placeholder="e.g. 12" />
        </Field>
      </div>

      {/* Duration */}
      <CourseDurationFields value={form} onChange={patch => setForm(f => ({ ...f, ...patch }))} />

      {/* Pricing */}
      <div>
        <Label className="text-sm font-medium text-gray-700">Pricing</Label>
        <div className="flex gap-1 mt-1.5 mb-3 rounded-lg bg-gray-100 p-1 w-fit">
          {(['PerPerson', 'FlatFee'] as const).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setForm(f => ({ ...f, pricingModel: m }))}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer ${
                form.pricingModel === m ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {m === 'PerPerson' ? 'Per person' : 'Flat fee'}
            </button>
          ))}
        </div>
        {form.pricingModel === 'PerPerson' ? (
          <Field id="price" label={`Price per person (${symbol})`}>
            <Input id="price" type="number" min="0" step="0.01" value={form.price ?? ''} onChange={e => setNum('price', e.target.value)} placeholder="0.00" className="max-w-xs" />
          </Field>
        ) : (
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <Field id="flatFeeAmount" label={`Flat fee (${symbol}) *`}>
              <Input id="flatFeeAmount" type="number" min="0" step="0.01" value={form.flatFeeAmount ?? ''} onChange={e => setNum('flatFeeAmount', e.target.value)} placeholder="e.g. 500.00" required />
            </Field>
            <Field id="flatFeeCapacity" label="Up to how many people?">
              <Input id="flatFeeCapacity" type="number" min="1" step="1" value={form.flatFeeCapacity ?? ''} onChange={e => setInt('flatFeeCapacity', e.target.value)} placeholder="e.g. 12" />
            </Field>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-1.5">
          {form.pricingModel === 'FlatFee'
            ? 'Charged once per booking regardless of how many people attend. The capacity is shown to describe the package but isn’t enforced as a hard limit.'
            : 'Multiplied by the number of places booked.'}
        </p>
      </div>

      {/* Description */}
      <Field id="description" label="Description">
        <textarea
          id="description"
          value={form.description ?? ''}
          onChange={e => set('description', e.target.value)}
          placeholder="Brief description of the course content…"
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
        />
      </Field>

      {/* Website URL */}
      <Field id="websiteUrl" label="Your own page for this course (optional)">
        <Input
          id="websiteUrl"
          type="text"
          inputMode="url"
          value={form.websiteUrl ?? ''}
          onChange={e => { set('websiteUrl', e.target.value); setOwnPageError(null) }}
          onBlur={() => {
            // Show the address as it will be saved, with https:// added if it was left off.
            const ownPage = normaliseOwnPage(form.websiteUrl)
            setOwnPageError(ownPage.error)
            if (!ownPage.error) setForm(f => ({ ...f, websiteUrl: ownPage.value }))
          }}
          placeholder="https://example.com/courses/my-course"
          aria-invalid={ownPageError ? true : undefined}
          aria-describedby="websiteUrl-help"
        />
        {ownPageError && <p className="text-xs text-red-600" role="alert">{ownPageError}</p>}
        <p id="websiteUrl-help" className="text-xs text-muted-foreground">
          The page for this course on your own website, if it has one. It is used as the "More about this course" link in booking emails.
          On the Pro plan it also makes the embedded course cards link to your page, and asks search engines not to list the CertSlate-hosted page for this course, so there is only one.
        </p>
      </Field>

      {/* Identity check at the register */}
      <div className="border-t border-gray-100 pt-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={form.requirePhotoCheck}
            onChange={e => setForm(f => ({ ...f, requirePhotoCheck: e.target.checked }))}
          />
          <span>
            <span className="text-sm font-medium text-gray-900">Ask trainers to check each attendee's ID</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              At the register the trainer compares each person with their photo and ticks "ID checked". It only prompts:
              it never stops anyone being marked present or the register being completed.
              {!photoSettings.enabled && <span className="block text-amber-700 mt-1">Photos are switched off, so trainers won't see this yet. Turn them on in Settings → Photos.</span>}
            </span>
          </span>
        </label>
      </div>

      {/* Third-party certification */}
      <div className="border-t border-gray-100 pt-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={form.isThirdPartyCertified}
            onChange={e => {
              if (e.target.checked && !isPro) { setShowProDialog(true); return }
              setForm(f => ({ ...f, isThirdPartyCertified: e.target.checked }))
            }}
          />
          <span>
            <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
              Certified by a third party
              {!isPro && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Pro</span>}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">
              The certificate is issued by an external awarding body, not generated by CertSlate.
              You'll upload and assign each certificate yourself once it arrives.
            </span>
          </span>
        </label>

        {form.isThirdPartyCertified && (
          <div className="mt-4 pl-7 space-y-4">
            <Field id="thirdPartyProviderName" label="Provider name *">
              <Input
                id="thirdPartyProviderName"
                value={form.thirdPartyProviderName ?? ''}
                onChange={e => set('thirdPartyProviderName', e.target.value)}
                placeholder="e.g. NEBOSH"
                required
                className="max-w-xs"
              />
            </Field>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={form.sendThirdPartyCompletionEmail}
                onChange={e => setForm(f => ({ ...f, sendThirdPartyCompletionEmail: e.target.checked }))}
              />
              <span className="text-sm text-gray-700">
                Email the learner when they complete this course
                <span className="block text-xs text-gray-500 mt-0.5">
                  A "thanks for completing" email, since there's no certificate to attach yet.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Certificate design */}
      {!form.isThirdPartyCertified && (
        <div className="border-t border-gray-100 pt-4">
          <Field id="certificateDesignId" label="Certificate design">
            <select
              id="certificateDesignId"
              value={form.certificateDesignId ?? ''}
              onChange={e => setForm(f => ({ ...f, certificateDesignId: e.target.value || null }))}
              className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">— No certificate —</option>
              {designs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {designs.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No designs yet — create one in the Certificates section first.</p>
            )}
          </Field>
          {selectedDesign && (
            <div className="mt-3">
              <CertificateDesignPreview design={selectedDesign} />
            </div>
          )}
        </div>
      )}

      <UnsavedBar
        visible={dirty}
        saved={saved}
        error={updateMutation.isError ? categoryErrorMessage(updateMutation.error, 'Could not save. Please try again.') : null}
        isPending={updateMutation.isPending}
        onCancel={() => { setForm(baseline); setOwnPageError(null); updateMutation.reset() }}
      />
    </form>
    <ProUpgradeDialog
      open={showProDialog}
      onClose={() => setShowProDialog(false)}
      featureName="Third-party certification"
    />
    </>
  )
}

// ─── Materials tab ────────────────────────────────────────────────────────────

function AddLinkForm({ courseId, onDone }: { courseId: string; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')

  const mutation = useMutation({
    mutationFn: () => adminApi.addCourseMaterialLink(courseId, { title, url }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course-materials', courseId] })
      onDone()
    },
  })

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-900">Add link</p>
        <button type="button" onClick={onDone} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={14} /></button>
      </div>
      <form onSubmit={e => { e.preventDefault(); mutation.mutate() }} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="link-title">Title *</Label>
          <Input id="link-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. YouTube walkthrough" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="link-url">URL *</Label>
          <Input id="link-url" type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" required />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onDone}>Cancel</Button>
          <Button type="submit" size="sm" disabled={mutation.isPending}>{mutation.isPending ? 'Adding…' : 'Add link'}</Button>
        </div>
      </form>
    </Card>
  )
}

function UploadFileForm({ courseId, onDone }: { courseId: string; onDone: () => void }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploadPct, setUploadPct] = useState(0)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => {
      setUploadPct(0); setErrorMsg(null)
      return adminApi.uploadCourseMaterial(courseId, file!, title || file!.name, setUploadPct)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course-materials', courseId] })
      onDone()
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err as { response?: { data?: { title?: string } } })?.response?.data?.title ??
        'Upload failed. Check that the server is running and try again.'
      setErrorMsg(msg)
    },
  })

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-900">Upload file</p>
        <button type="button" onClick={onDone} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={14} /></button>
      </div>
      <form onSubmit={e => { e.preventDefault(); if (file) mutation.mutate() }} className="space-y-3">
        <div className="space-y-1.5">
          <Label>File *</Label>
          <div
            className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-300 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <FileText size={16} />
                <span className="font-medium">{file.name}</span>
                <span className="text-gray-400">({formatBytes(file.size)})</span>
              </div>
            ) : (
              <div>
                <Upload size={20} className="mx-auto text-gray-300 mb-1" />
                <p className="text-sm text-gray-500">Click to choose a file</p>
                <p className="text-xs text-gray-400 mt-0.5">PDF, Word, PowerPoint, images — up to {MAX_FILE_MB} MB</p>
              </div>
            )}
          </div>
          <input ref={fileInputRef} type="file" className="hidden"
            onChange={e => {
              const f = e.target.files?.[0]
              if (!f) return
              if (f.size > MAX_FILE_MB * 1024 * 1024) {
                setErrorMsg(`File exceeds the ${MAX_FILE_MB} MB limit. For videos, add a YouTube or external link instead.`)
                e.target.value = ''; return
              }
              setErrorMsg(null); setFile(f)
              if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''))
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="file-title">Display title</Label>
          <Input id="file-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Defaults to filename" />
        </div>
        {file?.name.match(/\.pptx?$/i) && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded px-3 py-2">
            PowerPoint files are available for download only. Export as PDF to enable in-browser viewing.
          </p>
        )}
        {mutation.isPending && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500"><span>Uploading…</span><span>{uploadPct}%</span></div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-150" style={{ width: `${uploadPct}%` }} />
            </div>
          </div>
        )}
        {errorMsg && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{errorMsg}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onDone} disabled={mutation.isPending}>Cancel</Button>
          <Button type="submit" size="sm" disabled={!file || mutation.isPending}>Upload</Button>
        </div>
      </form>
    </Card>
  )
}

function MaterialRow({ material, courseId }: { material: CourseMaterialDto; courseId: string }) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [loadingUrl, setLoadingUrl] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.deleteCourseMaterial(courseId, material.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['course-materials', courseId] }),
  })

  const fetchUrl = async () => {
    if (downloadUrl) return downloadUrl
    setLoadingUrl(true)
    try {
      const res = await adminApi.getCourseMaterialDownloadUrl(courseId, material.id)
      setDownloadUrl(res.data.url)
      return res.data.url
    } finally { setLoadingUrl(false) }
  }

  const isLink = material.materialType === 'Link'
  const isEmbeddableYt = isLink && material.url && isYouTubeUrl(material.url)
  const isViewablePdf = !isLink && isPdf(material.contentType)

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
        <div className="shrink-0">
          {isLink ? <Link2 size={16} className="text-indigo-500" /> : <MaterialIcon contentType={material.contentType} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{material.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {isLink
              ? material.url
              : `${material.fileName ?? 'File'}${material.fileSizeBytes ? ` · ${formatBytes(material.fileSizeBytes)}` : ''}`
            }
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {(isEmbeddableYt || isViewablePdf) && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
              onClick={() => setExpanded(v => !v)}>
              {expanded ? 'Hide' : 'Preview'}
            </Button>
          )}
          {isLink ? (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-indigo-600"
              onClick={() => window.open(material.url!, '_blank', 'noopener')} title="Open link">
              <ExternalLink size={14} />
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-indigo-600"
              onClick={async () => window.open(await fetchUrl(), '_blank', 'noopener')} disabled={loadingUrl} title="Download">
              <Download size={14} />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-300 hover:text-red-500"
            onClick={() => { if (confirm(`Remove "${material.title}"?`)) deleteMutation.mutate() }}
            disabled={deleteMutation.isPending} title="Delete">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          {isEmbeddableYt && material.url && (() => {
            const embedUrl = youTubeEmbedUrl(material.url)
            return embedUrl ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe src={embedUrl} className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen title={material.title} />
              </div>
            ) : null
          })()}
          {isViewablePdf && (
            <div className="p-3">
              {downloadUrl
                ? <iframe src={downloadUrl} className="w-full rounded border border-gray-200" style={{ height: 500 }} title={material.title} />
                : <Button size="sm" variant="outline" onClick={async () => { await fetchUrl(); setExpanded(true) }} disabled={loadingUrl}>
                    {loadingUrl ? 'Loading…' : 'Load preview'}
                  </Button>
              }
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MaterialsTab({ courseId }: { courseId: string }) {
  const [adding, setAdding] = useState<'file' | 'link' | null>(null)

  const { data: materials = [], isLoading } = useQuery({
    queryKey: ['course-materials', courseId],
    queryFn: () => adminApi.getCourseMaterials(courseId).then(r => r.data),
  })

  const usedBytes = materials.reduce((sum, m) => sum + (m.fileSizeBytes ?? 0), 0)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Course Materials</CardTitle>
            <p className="text-xs text-gray-400 mt-1">
              Files up to {MAX_FILE_MB} MB each, {MAX_COURSE_MB} MB total per course. Links have no size limit.
              {usedBytes > 0 && ` ${formatBytes(usedBytes)} of ${MAX_COURSE_MB} MB used.`}
            </p>
          </div>
          {adding === null && materials.length > 0 && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setAdding('link')}>
                <Link2 size={14} /> Add link
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setAdding('file')}>
                <Upload size={14} /> Upload file
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding === 'link' && <AddLinkForm courseId={courseId} onDone={() => setAdding(null)} />}
        {adding === 'file' && <UploadFileForm courseId={courseId} onDone={() => setAdding(null)} />}

        {isLoading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : materials.length === 0 && adding === null ? (
          <div className="py-10 text-center">
            <BookOpen size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium text-sm">No materials yet</p>
            <p className="text-xs text-gray-400 mt-1">Upload handouts, PDFs, or add links to videos and resources.</p>
            <div className="flex justify-center gap-2 mt-4">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAdding('link')}>
                <Link2 size={14} /> Add link
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAdding('file')}>
                <Upload size={14} /> Upload file
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {materials.map(m => <MaterialRow key={m.id} material={m} courseId={courseId} />)}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Trainers tab ─────────────────────────────────────────────────────────────

function TrainersTab({ courseId }: { courseId: string }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [pendingId, setPendingId] = useState<string | null>(null)

  const { data: trainers = [], isLoading } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ trainerId, qualify }: { trainerId: string; qualify: boolean }) => {
      const currentIds = trainers.filter(t => t.qualifiedCourseIds.includes(courseId)).map(t => t.id)
      const nextIds = qualify ? [...currentIds, trainerId] : currentIds.filter(id => id !== trainerId)
      return adminApi.setCourseQualifiedTrainers(courseId, nextIds)
    },
    onMutate: ({ trainerId }) => setPendingId(trainerId),
    onSettled: () => setPendingId(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainers'] }),
  })

  const filtered = trainers.filter(t =>
    !search.trim() || `${t.firstName} ${t.lastName}`.toLowerCase().includes(search.toLowerCase().trim())
  )
  const qualifiedCount = trainers.filter(t => t.qualifiedCourseIds.includes(courseId)).length

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base">Qualified Trainers</CardTitle>
            <p className="text-xs text-gray-400 mt-1">
              {qualifiedCount} of {trainers.length} trainer{trainers.length === 1 ? '' : 's'} qualified to deliver this course.
            </p>
          </div>
          {trainers.length > 5 && (
            <div className="relative w-56">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trainers…" className="pl-8 h-9" />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : trainers.length === 0 ? (
          <div className="py-10 text-center">
            <UserCheck size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium text-sm">No trainers yet</p>
            <p className="text-xs text-gray-400 mt-1">Add trainers in the Trainers section first.</p>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No trainers match your search.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(t => {
              const isQualified = t.qualifiedCourseIds.includes(courseId)
              const isPending = pendingId === t.id && toggleMutation.isPending
              return (
                <label
                  key={t.id}
                  className="flex items-center justify-between gap-3 py-2.5 cursor-pointer"
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className={`flex items-center justify-center h-5 w-5 rounded border shrink-0 transition-colors ${
                      isQualified ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                    }`}>
                      {isQualified && <Check size={13} className="text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="text-sm font-medium text-gray-900">{t.firstName} {t.lastName}</span>
                      {!t.isActive && <span className="ml-2 text-xs text-gray-400">Inactive</span>}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isQualified}
                    disabled={isPending}
                    onChange={e => toggleMutation.mutate({ trainerId: t.id, qualify: e.target.checked })}
                  />
                  {isPending && <span className="text-xs text-gray-400">Saving…</span>}
                </label>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Tab = 'details' | 'materials' | 'trainers' | 'website' | 'live'

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState<Tab>('details')
  // The live activities tab reports unsaved edits, because switching tab would otherwise throw them away.
  const [unsaved, setUnsaved] = useState({ details: false, website: false, live: false })
  const reportDetails = useCallback((d: boolean) => setUnsaved(s => (s.details === d ? s : { ...s, details: d })), [])
  const reportWebsite = useCallback((d: boolean) => setUnsaved(s => (s.website === d ? s : { ...s, website: d })), [])
  const reportLive = useCallback((d: boolean) => setUnsaved(s => (s.live === d ? s : { ...s, live: d })), [])
  // Leaving the page (nav bar, links, closing the browser) also asks. Live activities guards itself.
  useUnsavedChangesGuard(unsaved.details || unsaved.website)
  const changeTab = (next: Tab) => {
    // Details, Website and Live activities hold edits that switching tab would throw away.
    const unsavedHere = (unsaved as Record<string, boolean>)[tab]
    if (next !== tab && unsavedHere && !window.confirm('You have unsaved changes. Leave without saving?')) return
    setTab(next)
  }

  const { data: course, isLoading } = useQuery({
    queryKey: ['course', id],
    queryFn: () => adminApi.getCourse(id!).then(r => r.data),
    enabled: !!id,
  })

  // Same query the rest of the admin app uses for the tenant's region — also tells us whether CertSlate Live is on.
  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  useTopBar(
    <Breadcrumb items={[
      { label: 'Courses', to: '/admin/courses' },
      { label: course?.name ?? '…' },
    ]} />
  )

  if (isLoading) return <p className="text-gray-500">Loading…</p>
  if (!course) return <p className="text-gray-500">Course not found.</p>

  const tabs: { id: Tab; label: string }[] = [
    { id: 'details',   label: 'Details' },
    { id: 'materials', label: 'Materials' },
    { id: 'trainers',  label: 'Trainers' },
    { id: 'website',   label: 'Website' },
    ...(profile?.liveDeliveryEnabled ? [{ id: 'live' as Tab, label: 'Live activities' }] : []),
  ]

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {course.code && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
              {course.code}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => changeTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer border-b-2 -mb-px ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'details'   && <DetailsTab course={course} onDirtyChange={reportDetails} />}
      {tab === 'materials' && <MaterialsTab courseId={id!} />}
      {tab === 'trainers'  && <TrainersTab courseId={id!} />}
      {tab === 'website'   && <WebsiteTab courseId={id!} onOpenDetails={() => changeTab('details')} onDirtyChange={reportWebsite} />}
      {tab === 'live' && profile?.liveDeliveryEnabled && <CourseLiveActivitiesTab courseId={id!} onDirtyChange={reportLive} />}
    </div>
  )
}
